import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../ui/button";
import { X } from "lucide-react";

const STEPS = [
  { sel: ['[data-testid="safe-to-spend"]'], title: "Your Safe to Spend", body: "This is the money genuinely yours to spend — what's left after your upcoming commitments and the reserve you set aside.", pad: 14 },
  { sel: ['[data-testid="ratio-card"]'], title: "Commitment Ratio", body: "The share of your income already promised to recurring commitments. Lower is healthier — we colour it for you at a glance.", pad: 10 },
  { sel: ['[data-testid="upcoming-commitments"]'], title: "What's coming up", body: "Every upcoming payment in one place, so a big debit never takes you by surprise.", pad: 10 },
  { sel: ['[data-testid="nav-calendar"]', '[data-testid="bottomnav-calendar"]'], title: "Your Calendar", body: "See exactly what leaves your account and when across the whole month.", pad: 8 },
  { sel: ['[data-testid="notifications-bell"]'], title: "Stay ahead", body: "Turn on reminders here — COMMITMENTT nudges you before big payments are due.", pad: 8 },
  { sel: ['[data-testid="sidebar-add-btn"]', '[data-testid="mobile-add-btn"]'], title: "Add in seconds", body: "Tap Add to enter a commitment manually, or paste a message, screenshot or PDF statement and we'll read it for you.", pad: 8 },
];

function findEl(sels) {
  for (const s of sels) {
    const el = document.querySelector(s);
    if (el && el.offsetParent !== null) return el;
  }
  return null;
}

export default function SpotlightTour({ open, onClose }) {
  const { refresh } = useAuth();
  const nav = useNavigate();
  const [i, setI] = useState(0);
  const [rect, setRect] = useState(null);
  const [ready, setReady] = useState(false);

  const finish = useCallback(async () => {
    try { await api.post("/tour/complete"); refresh && refresh(); } catch (e) {}
    onClose();
  }, [onClose, refresh]);

  useEffect(() => {
    if (open) {
      setI(0);
      if (window.location.pathname !== "/app") nav("/app");
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    let timer = null;
    setReady(false);
    let tries = 0;
    const locate = () => {
      if (cancelled) return;
      const step = STEPS[i];
      const el = findEl(step.sel);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        timer = setTimeout(() => {
          if (cancelled) return;
          setRect(el.getBoundingClientRect());
          setReady(true);
        }, 320);
      } else if (tries < 16) {
        tries += 1;
        timer = setTimeout(locate, 140);
      } else if (i < STEPS.length - 1) {
        setI((v) => v + 1);
      } else {
        finish();
      }
    };
    locate();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [open, i, finish]);

  useEffect(() => {
    if (!open || !ready) return;
    const update = () => {
      const el = findEl(STEPS[i].sel);
      if (el) setRect(el.getBoundingClientRect());
    };
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [open, ready, i]);

  if (!open || !rect || !STEPS[i]) return null;

  const step = STEPS[i];
  const pad = step.pad || 10;
  const last = i === STEPS.length - 1;

  const boxTop = rect.top - pad;
  const boxLeft = rect.left - pad;
  const boxW = rect.width + pad * 2;
  const boxH = rect.height + pad * 2;

  const vh = window.innerHeight;
  const vw = window.innerWidth;
  const cardW = Math.min(340, vw - 32);
  const spaceBelow = vh - (boxTop + boxH);
  const placeBelow = spaceBelow > 210;
  const cardTop = placeBelow ? boxTop + boxH + 14 : Math.max(16, boxTop - 210);
  let cardLeft = boxLeft + boxW / 2 - cardW / 2;
  cardLeft = Math.max(16, Math.min(cardLeft, vw - cardW - 16));

  return createPortal(
    <div className="fixed inset-0 z-[70]" data-testid="spotlight-tour">
      {/* Dimmer with cut-out highlight */}
      <div
        className="fixed rounded-2xl pointer-events-none transition-all duration-300"
        style={{
          top: boxTop, left: boxLeft, width: boxW, height: boxH,
          boxShadow: "0 0 0 9999px rgba(15,42,67,0.72)",
          outline: "2px solid rgba(16,185,129,0.9)",
          outlineOffset: "2px",
        }}
      />
      {/* Click-catch to advance */}
      <div className="fixed inset-0" onClick={() => (last ? finish() : setI(i + 1))} />

      {/* Tooltip card */}
      <div
        className="fixed bg-white rounded-2xl shadow-card-lg p-5 animate-fade-up"
        style={{ top: cardTop, left: cardLeft, width: cardW }}
        onClick={(e) => e.stopPropagation()}
      >
        <button onClick={finish} aria-label="Skip tour" data-testid="tour-skip"
          className="absolute top-3 right-3 text-muted-foreground hover:text-ink">
          <X size={16} />
        </button>
        <p className="text-[11px] font-semibold tracking-wide text-mint">{i + 1} OF {STEPS.length}</p>
        <h2 className="font-heading text-lg font-medium text-ink mt-1" data-testid="tour-title">{step.title}</h2>
        <p className="text-sm text-slatey mt-1.5 leading-relaxed">{step.body}</p>

        <div className="flex items-center gap-1.5 mt-4" data-testid="tour-progress">
          {STEPS.map((_, idx) => (
            <span key={idx} className={`h-1.5 rounded-full transition-all ${idx === i ? "w-6 bg-navy" : "w-1.5 bg-border"}`} />
          ))}
        </div>

        <div className="flex items-center justify-between mt-4">
          <button onClick={finish} className="text-sm text-muted-foreground hover:text-ink" data-testid="tour-skip-text">Skip</button>
          <div className="flex gap-2">
            {i > 0 && <Button variant="secondary" onClick={() => setI(i - 1)} className="rounded-full bg-secondary text-ink h-9" data-testid="tour-back">Back</Button>}
            {last
              ? <Button onClick={finish} className="rounded-full bg-navy text-white hover:bg-navy-light h-9" data-testid="tour-finish">Done</Button>
              : <Button onClick={() => setI(i + 1)} className="rounded-full bg-navy text-white hover:bg-navy-light h-9" data-testid="tour-next">Next</Button>}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
